import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  ShieldCheck,
  CreditCard,
  Calendar,
  Edit,
  Trash2,
  ExternalLink,
  MessageSquare,
  Plus,
  TrendingUp,
  Briefcase,
  Repeat,
  Clock,
} from 'lucide-react';

import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Avatar } from '@/shared/components/ui/Avatar';
import { formatINR } from '@/shared/utils/currency';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { useUser } from '@/shared/hooks/useAuth';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { ROUTES } from '@/routes/paths';

import { useContact, useAccount360, useContactMutations } from '../hooks/useContacts';
import { useDeals } from '@/features/deals/hooks/useDeals';
import { SubContactFormModal } from '../components/SubContactFormModal';
import { ContactTrackingPanel } from '../components/ContactTrackingPanel';
import type { SubContact } from '../types';

// Admin tier that can close a reassignment request left pending from before
// contact reassignment was retired.
const CONTACT_ADMIN_ROLES = new Set(['superadmin', 'admin', 'ceo', 'owner', 'department_admin']);

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtMoney(val?: number | null) {
  if (val === undefined || val === null) return '—';
  return formatINR(val);
}

function fmtAddress(addr?: string, city?: string, state?: string, pin?: string, country?: string): string {
  return [addr, city, state, pin, country].filter(Boolean).join(', ');
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const canUpdate = useHasPermission('contacts.update');
  const canDelete = useHasPermission('contacts.delete');
  const currentUser = useUser();
  const isContactAdmin = Boolean(currentUser && CONTACT_ADMIN_ROLES.has(currentUser.role));

  const { data: contact, isLoading, isError, refetch } = useContact(id);
  const { data: account360 } = useAccount360(id);
  const { data: dealsData } = useDeals({ contact_id: id });
  const { data: userOptions } = useUserOptions(true);
  const { deleteContact, updateContactExtended, decideReassignment } = useContactMutations();
  // Who added / had / works on a contact is the Super Admin's to see.
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [subContactModalOpen, setSubContactModalOpen] = useState(false);
  const [editingSubContact, setEditingSubContact] = useState<{ sub: SubContact; index: number } | null>(null);

  const [editingPreviousCredit, setEditingPreviousCredit] = useState(false);
  const [prevCreditForm, setPrevCreditForm] = useState({
    balance: 0,
    dueDate: '',
    asOfDate: '',
    notes: '',
  });

  const pendingReassignment = contact?.reassignmentRequest?.status === 'pending' ? contact.reassignmentRequest : null;
  const reassignToName = (() => {
    const to = pendingReassignment?.toUserId;
    if (to && typeof to === 'object') return to.display_name || to.username || 'another BDM';
    return userOptions?.find((u) => u.id === to)?.display_name || 'another BDM';
  })();
  const reassignRequestedByName = (() => {
    const by = pendingReassignment?.requestedBy;
    if (by && typeof by === 'object') return by.display_name || by.username || 'A colleague';
    return userOptions?.find((u) => u.id === by)?.display_name || 'A colleague';
  })();

  const handleReassignmentDecision = (decision: 'approve' | 'reject') => {
    if (!id) return;
    decideReassignment.mutate(
      { id, decision },
      {
        onSuccess: (data) => {
          toast.success(
            decision === 'approve'
              ? `Reassigned — ${data.cascade.pipelineRecords} pipeline record(s), ${data.cascade.orders} order(s), ${data.cascade.invoices} invoice(s) moved.`
              : 'Reassignment request rejected.'
          );
          refetch();
        },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not decide this request.'),
      }
    );
  };

  const handleDelete = () => {
    if (!id) return;
    deleteContact.mutate(id, {
      onSuccess: () => {
        toast.success('Contact deleted.');
        navigate(ROUTES.contacts);
      },
      onError: () => toast.error('Failed to delete contact.'),
    });
  };

  const handleSaveSubContact = (payload: SubContact) => {
    if (!contact || !id) return;
    const current = contact.subContacts ?? [];
    let updated: SubContact[];
    if (editingSubContact !== null) {
      updated = current.map((s, i) => (i === editingSubContact.index ? { ...s, ...payload } : s));
    } else {
      updated = [...current, payload];
    }

    updateContactExtended.mutate(
      { id, payload: { subContacts: updated } },
      {
        onSuccess: () => {
          toast.success(editingSubContact !== null ? 'Sub-contact updated.' : 'Sub-contact added.');
          setSubContactModalOpen(false);
          setEditingSubContact(null);
          refetch();
        },
        onError: () => toast.error('Failed to save sub-contact.'),
      }
    );
  };

  const handleDeleteSubContact = (index: number) => {
    if (!contact || !id) return;
    const updated = (contact.subContacts ?? []).filter((_, i) => i !== index);
    updateContactExtended.mutate(
      { id, payload: { subContacts: updated } },
      {
        onSuccess: () => {
          toast.success('Sub-contact removed.');
          refetch();
        },
        onError: () => toast.error('Failed to remove sub-contact.'),
      }
    );
  };

  const handleOpenEditPrevCredit = () => {
    if (!contact) return;
    setPrevCreditForm({
      balance: contact.previousCreditBalance || 0,
      dueDate: contact.previousCreditDueDate ? new Date(contact.previousCreditDueDate).toISOString().split('T')[0] : '',
      asOfDate: contact.previousCreditAsOfDate ? new Date(contact.previousCreditAsOfDate).toISOString().split('T')[0] : '',
      notes: contact.previousCreditNotes || '',
    });
    setEditingPreviousCredit(true);
  };

  const handleSavePrevCredit = async () => {
    if (!id) return;
    try {
      await updateContactExtended.mutateAsync({
        id,
        payload: {
          previousCreditBalance: Number(prevCreditForm.balance) || 0,
          previousCreditDueDate: prevCreditForm.dueDate ? new Date(prevCreditForm.dueDate).toISOString() : null,
          previousCreditAsOfDate: prevCreditForm.asOfDate ? new Date(prevCreditForm.asOfDate).toISOString() : null,
          previousCreditNotes: prevCreditForm.notes,
        },
      });
      toast.success('Pre-go-live legacy credit updated successfully!');
      setEditingPreviousCredit(false);
      refetch();
    } catch {
      toast.error('Failed to update previous credit.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (isError || !contact) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-12">
        <p className="text-body-base text-on-surface-variant font-medium">Contact record not found.</p>
        <Button variant="outline" onClick={() => navigate(ROUTES.contacts)}>
          Back to Directory
        </Button>
      </div>
    );
  }

  const cleanPhone = (contact.phone || '').replace(/[^0-9]/g, '');
  const cleanWhatsapp = (contact.whatsapp || contact.phone || '').replace(/[^0-9]/g, '');
  const extraPhones = contact.extraPhones ?? [];
  const importedDetails = contact.additionalInfoValues?.filter?.(
    (item): item is { key: string; value: string } => Boolean(item?.key && item?.value)
  ) ?? [];

  return (
    <div className="w-full flex-1 flex flex-col gap-6 p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* ── Top Header & Actions ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-outline/60 pb-5">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(ROUTES.contacts)}
            aria-label="Back to Contacts"
            className="rounded-xl h-10 w-10 border-outline bg-surface"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Avatar name={contact.company || contact.name} size="lg" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-body-xs font-semibold uppercase tracking-wider text-primary">CRM Directory</span>
                <span className="text-body-xs text-on-surface-variant">/</span>
                <span className="text-body-xs text-on-surface-variant">{contact.name}</span>
              </div>
              <h1 className="text-2xl font-black tracking-tight text-on-surface flex items-center gap-2.5">
                {contact.company || contact.name}
              </h1>
              {contact.company && contact.name && (
                <p className="text-body-xs text-on-surface-variant font-medium">
                  Primary Contact: <span className="text-on-surface font-semibold">{contact.name}</span>
                  {contact.position && ` • ${contact.position}`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {cleanWhatsapp && (
            <a
              href={`https://wa.me/${cleanWhatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-success/10 text-success-text hover:bg-success/10 border border-success/30 transition"
            >
              <MessageSquare className="h-3.5 w-3.5" /> WhatsApp
            </a>
          )}

          {cleanPhone && (
            <a
              href={`tel:${cleanPhone}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-info/10 text-info-text hover:bg-info/10 border border-info/30 transition"
            >
              <Phone className="h-3.5 w-3.5" /> Call
            </a>
          )}

          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-muted text-on-surface-variant hover:bg-muted border border-border transition"
            >
              <Mail className="h-3.5 w-3.5" /> Email
            </a>
          )}

          {canUpdate && (
            <Button
              onClick={() => navigate(`/contacts/${id}/edit`)}
              className="gap-1.5 shadow-xs"
            >
              <Edit className="h-4 w-4" /> Edit Contact
            </Button>
          )}

          {canDelete && (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(true)}
              className="text-error hover:bg-error/5 border-outline hover:border-error/40 gap-1.5"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* ── Key Profile & Classification Badges ──────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap bg-surface p-3.5 rounded-2xl border border-outline shadow-xs">
        <span className="text-body-xs font-semibold text-on-surface-variant mr-1">Classification:</span>
        <Badge tone="primary" className="capitalize">
          {contact.contactType || 'Organization'}
        </Badge>
        <Badge tone="success" className="capitalize">
          {contact.customerType || 'Customer'}
        </Badge>
        {contact.category && (
          <Badge tone="warning" className="capitalize">
            {contact.category}
          </Badge>
        )}
        {isSuperAdmin && contact.creator_name && (
          <span className="ml-auto text-body-xs text-on-surface-variant">
            Created by <span className="font-semibold text-on-surface">{contact.creator_name}</span> on {fmtDate(contact.created_at)}
          </span>
        )}
      </div>

      {/* ── A reassignment request left pending from before reassignment was
          retired. Only an admin sees it, to close it; nothing is locked. ── */}
      {pendingReassignment && isContactAdmin && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-warning/40 bg-warning/10 p-4">
          <div className="flex items-center gap-2.5">
            <Repeat className="h-4 w-4 text-warning shrink-0" />
            <p className="text-body-sm text-on-surface">
              <span className="font-semibold">{reassignRequestedByName}</span> requested handing this contact to{' '}
              <span className="font-semibold">{reassignToName}</span> before reassignment was retired — approve or reject to close it.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              disabled={decideReassignment.isPending}
              onClick={() => handleReassignmentDecision('reject')}
            >
              Reject
            </Button>
            <Button
              size="sm"
              disabled={decideReassignment.isPending}
              onClick={() => handleReassignmentDecision('approve')}
            >
              Approve
            </Button>
          </div>
        </div>
      )}

      {isSuperAdmin && id && <ContactTrackingPanel contactId={id} />}

      {/* ── Telemetry / Commercial KPI Bar ───────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-body-xs font-semibold text-on-surface-variant">Total Sales</span>
            <TrendingUp className="h-4 w-4 text-primary" />
          </div>
          <p className="mt-1.5 text-xl font-bold text-on-surface">
            {account360 ? fmtMoney(account360.sales.totalSales) : '—'}
          </p>
          <span className="text-data-xs text-on-surface-variant font-medium">
            {account360?.sales.totalOrders || 0} Total Orders
          </span>
        </div>

        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-body-xs font-semibold text-on-surface-variant">Current Outstanding</span>
            <CreditCard className="h-4 w-4 text-warning" />
          </div>
          <p className="mt-1.5 text-xl font-bold text-on-surface">
            {account360 ? fmtMoney(account360.sales.currentOutstanding) : '—'}
          </p>
          <span className="text-data-xs text-on-surface-variant font-medium">
            Credit Days: {contact.creditDays || 0} days
          </span>
        </div>

        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-body-xs font-semibold text-on-surface-variant">Credit Limit</span>
            <ShieldCheck className="h-4 w-4 text-success-text" />
          </div>
          <p className="mt-1.5 text-xl font-bold text-on-surface">
            {contact.creditLimit != null ? fmtMoney(contact.creditLimit) : 'No Limit'}
          </p>
          <span className="text-data-xs text-on-surface-variant font-medium">
            Terms: {contact.paymentTerms || 'Standard'}
          </span>
        </div>

        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-body-xs font-semibold text-on-surface-variant">Pipeline Status</span>
            <Briefcase className="h-4 w-4 text-primary" />
          </div>
          <p className="mt-1.5 text-xl font-bold text-on-surface">
            {account360 ? `${account360.pipeline.won} Won / ${account360.pipeline.lost} Lost` : '—'}
          </p>
          <span className="text-data-xs text-on-surface-variant font-medium">
            {account360?.pipeline.enquiries || 0} Enquiries • {account360?.pipeline.quotations || 0} Quotes
          </span>
        </div>
      </div>

      {/* ── Main Details Grid (4 Sections) ───────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Basic Details & Location */}
        <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-6 shadow-xs">
          <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
            <User className="h-4 w-4 text-primary" />
            <h2 className="text-body-base font-semibold text-on-surface">Basic &amp; Contact Details</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3.5 gap-x-6 text-body-sm">
            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Contact Person</span>
              <span className="font-semibold text-on-surface">{contact.name || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Company Name</span>
              <span className="font-semibold text-on-surface">{contact.company || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Phone Number</span>
              <span className="font-semibold text-on-surface tabular-nums">{contact.phone || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">WhatsApp Number</span>
              <span className="font-semibold text-on-surface tabular-nums">{contact.whatsapp || contact.phone || '—'}</span>
            </div>

            <div className="sm:col-span-2">
              <span className="text-body-xs text-on-surface-variant block font-medium">Additional Mobile Numbers</span>
              {extraPhones.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-2">
                  {extraPhones.map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
                      className="rounded-lg border border-outline px-2 py-1 text-xs font-semibold tabular-nums text-primary hover:bg-primary/5"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              ) : (
                <span className="font-semibold text-on-surface">—</span>
              )}
            </div>

            <div className="sm:col-span-2">
              <span className="text-body-xs text-on-surface-variant block font-medium">Email Address</span>
              <span className="font-semibold text-on-surface">{contact.email || '—'}</span>
            </div>

            <div className="sm:col-span-2 border-t border-outline/60 pt-3">
              <span className="text-body-xs text-on-surface-variant block font-medium">Complete Address</span>
              <span className="font-semibold text-on-surface">
                {fmtAddress(contact.address, contact.city, contact.state, contact.pin, contact.country) || '—'}
              </span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">State / UT</span>
              <span className="font-semibold text-on-surface">{contact.state || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">City</span>
              <span className="font-semibold text-on-surface">{contact.city || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Pincode</span>
              <span className="font-semibold text-on-surface tabular-nums">{contact.pin || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Country</span>
              <span className="font-semibold text-on-surface">{contact.country || 'India'}</span>
            </div>
          </div>
        </div>

        {/* Card 2: Statutory & Additional Details */}
        <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-6 shadow-xs">
          <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <h2 className="text-body-base font-semibold text-on-surface">Additional &amp; Statutory Details</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3.5 gap-x-6 text-body-sm">
            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">GSTIN</span>
              <span className="font-mono font-bold text-on-surface">{contact.gstin || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">ECOM GSTIN</span>
              <span className="font-mono font-bold text-on-surface">{contact.ecomGstin || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">PAN Number</span>
              <span className="font-mono font-bold text-on-surface">{contact.panNo || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Aadhar Number</span>
              <span className="font-mono font-bold text-on-surface">{contact.aadharNo || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Gender</span>
              <span className="font-semibold text-on-surface capitalize">{contact.gender || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Position / Role</span>
              <span className="font-semibold text-on-surface">{contact.position || '—'}</span>
            </div>

            <div className="sm:col-span-2 border-t border-outline/60 pt-3">
              <span className="text-body-xs text-on-surface-variant block font-medium">Notes &amp; Remarks</span>
              <p className="font-medium text-on-surface text-body-xs mt-0.5 whitespace-pre-wrap">
                {contact.notes || 'No remarks recorded.'}
              </p>
            </div>
          </div>
        </div>

        {/* Card 3: Account & Commercial Balances */}
        <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-6 shadow-xs">
          <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
            <CreditCard className="h-4 w-4 text-primary" />
            <h2 className="text-body-base font-semibold text-on-surface">Account Balances &amp; Credit Terms</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3.5 gap-x-6 text-body-sm">
            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Creditors Opening</span>
              <span className="font-semibold text-on-surface tabular-nums">
                {contact.creditorsOpening != null ? fmtMoney(contact.creditorsOpening) : '₹0'}
              </span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Debtors Opening</span>
              <span className="font-semibold text-on-surface tabular-nums">
                {contact.debtorsOpening != null ? fmtMoney(contact.debtorsOpening) : '₹0'}
              </span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Credit Days</span>
              <span className="font-semibold text-on-surface tabular-nums">{contact.creditDays || 0} Days</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Credit Limit</span>
              <span className="font-semibold text-on-surface tabular-nums">
                {contact.creditLimit != null ? fmtMoney(contact.creditLimit) : 'No Limit'}
              </span>
            </div>

            <div className="sm:col-span-2">
              <span className="text-body-xs text-on-surface-variant block font-medium">Payment Terms</span>
              <span className="font-semibold text-on-surface">{contact.paymentTerms || 'Standard Terms'}</span>
            </div>

            {/* Pre-Go-Live Legacy Credit (Point 10) */}
            <div className="sm:col-span-2 border-t border-outline/60 pt-3">
              <div className="flex items-center justify-between pb-2">
                <span className="text-body-xs font-bold text-primary flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Pre-Go-Live Legacy Credit (Data Before Go-Live Date)
                </span>
                {canUpdate && !editingPreviousCredit && (
                  <button
                    type="button"
                    onClick={handleOpenEditPrevCredit}
                    className="text-xs font-semibold text-primary hover:underline cursor-pointer"
                  >
                    Edit Legacy Credit
                  </button>
                )}
              </div>

              {editingPreviousCredit ? (
                <div className="rounded-xl border border-indigo-200 bg-primary-container/40 p-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <div>
                      <label className="text-data-xs font-bold text-on-surface-variant block">Legacy Credit Balance (₹)</label>
                      <input
                        type="number"
                        min="0"
                        value={prevCreditForm.balance}
                        onChange={(e) => setPrevCreditForm({ ...prevCreditForm, balance: Number(e.target.value) })}
                        className="w-full mt-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="text-data-xs font-bold text-on-surface-variant block">As of Date</label>
                      <input
                        type="date"
                        value={prevCreditForm.asOfDate}
                        onChange={(e) => setPrevCreditForm({ ...prevCreditForm, asOfDate: e.target.value })}
                        className="w-full mt-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-medium"
                      />
                    </div>
                    <div>
                      <label className="text-data-xs font-bold text-on-surface-variant block">Payment Due Date</label>
                      <input
                        type="date"
                        value={prevCreditForm.dueDate}
                        onChange={(e) => setPrevCreditForm({ ...prevCreditForm, dueDate: e.target.value })}
                        className="w-full mt-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-medium"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-data-xs font-bold text-on-surface-variant block">Historical Notes / Remarks</label>
                    <input
                      type="text"
                      placeholder="e.g. Opening balance from legacy Tally/Busy ledger shared by Shailesh"
                      value={prevCreditForm.notes}
                      onChange={(e) => setPrevCreditForm({ ...prevCreditForm, notes: e.target.value })}
                      className="w-full mt-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setEditingPreviousCredit(false)}
                      className="rounded-lg border border-border bg-card px-3 py-1 text-xs font-semibold text-on-surface-variant hover:bg-muted cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePrevCredit}
                      disabled={updateContactExtended.isPending}
                      className="rounded-lg bg-primary-container px-3 py-1 text-xs font-semibold text-white hover:bg-primary-container cursor-pointer"
                    >
                      {updateContactExtended.isPending ? 'Saving...' : 'Save Legacy Credit'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-border bg-muted/70 p-3 text-xs space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-on-surface-variant">Historical Credit Balance:</span>
                    <span className="font-bold text-on-surface tabular-nums">
                      {contact.previousCreditBalance != null && contact.previousCreditBalance > 0
                        ? fmtMoney(contact.previousCreditBalance)
                        : '₹0 (No legacy credit recorded)'}
                    </span>
                  </div>
                  {contact.previousCreditAsOfDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-on-surface-variant">As of Date:</span>
                      <span className="font-medium text-on-surface-variant">{fmtDate(contact.previousCreditAsOfDate)}</span>
                    </div>
                  )}
                  {contact.previousCreditDueDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-on-surface-variant">Due Date:</span>
                      <span className="font-medium text-warning-text">{fmtDate(contact.previousCreditDueDate)}</span>
                    </div>
                  )}
                  {contact.previousCreditNotes && (
                    <div className="pt-1 border-t border-border text-on-surface-variant text-data-xs">
                      <span className="font-semibold text-on-surface-variant">Remarks: </span>
                      {contact.previousCreditNotes}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card 4: Personal Milestones & Anniversaries */}
        <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-6 shadow-xs">
          <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
            <Calendar className="h-4 w-4 text-primary" />
            <h2 className="text-body-base font-semibold text-on-surface">Anniversaries &amp; Personal Info</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3.5 gap-x-6 text-body-sm">
            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Marital Status</span>
              <span className="font-semibold text-on-surface capitalize">{contact.maritalStatus || '—'}</span>
            </div>

            <div>
              <span className="text-body-xs text-on-surface-variant block font-medium">Birthdate</span>
              <span className="font-semibold text-on-surface">{fmtDate(contact.birthdate)}</span>
            </div>
          </div>
        </div>
      </div>

      {importedDetails.length > 0 && (
        <div className="rounded-card border border-border bg-card p-6 shadow-xs">
          <h2 className="text-body-base font-semibold text-on-surface">Imported Source Details</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {importedDetails.slice(0, 120).map((item: { key: string; value: string }, index: number) => (
              <div key={`${item.key}-${index}`} className="rounded-lg border border-outline/70 p-3">
                <span className="text-body-xs font-medium text-on-surface-variant">{item.key}</span>
                <p className="mt-1 break-words text-body-xs font-semibold text-on-surface whitespace-pre-wrap">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Sub-Contacts & Stakeholder Team ──────────────────────────────── */}
      <div className="rounded-card border border-border bg-card p-6 shadow-xs flex flex-col gap-4">
        <div className="flex items-center justify-between border-b border-outline/60 pb-4">
          <div>
            <h2 className="text-body-base font-bold text-on-surface flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              Sub-Contacts &amp; Department Personnel ({contact.subContacts?.length || 0})
            </h2>
            <p className="text-body-xs text-on-surface-variant mt-0.5">
              Specific contacts for Purchase, Accounts, Dispatch, and Plant Logistics
            </p>
          </div>

          {canUpdate && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditingSubContact(null);
                setSubContactModalOpen(true);
              }}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" /> Add Sub-Contact
            </Button>
          )}
        </div>

        {contact.subContacts && contact.subContacts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contact.subContacts.map((sub, index) => (
              <div
                key={index}
                className="p-4 rounded-xl border border-outline/80 bg-surface-container-low/50 flex flex-col gap-2 relative group hover:border-primary/40 transition"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-on-surface text-body-sm">{sub.name || 'Unnamed'}</h3>
                    {sub.designation && (
                      <p className="text-body-xs text-on-surface-variant font-medium">{sub.designation}</p>
                    )}
                  </div>
                  {sub.contactTypeTag && (
                    <Badge tone="neutral" className="capitalize text-data-xs">
                      {sub.contactTypeTag.replace('_', ' ')}
                    </Badge>
                  )}
                </div>

                <div className="flex flex-col gap-1 text-body-xs text-on-surface-variant mt-1">
                  {sub.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-3 w-3 text-primary" />
                      <a href={`tel:${sub.phone}`} className="hover:underline text-on-surface font-medium">
                        {sub.phone}
                      </a>
                    </div>
                  )}
                  {sub.email && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="h-3 w-3 text-primary" />
                      <a href={`mailto:${sub.email}`} className="hover:underline text-on-surface font-medium truncate">
                        {sub.email}
                      </a>
                    </div>
                  )}
                </div>

                {canUpdate && (
                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-outline/40 mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingSubContact({ sub, index });
                        setSubContactModalOpen(true);
                      }}
                      className="text-body-xs text-primary font-semibold hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSubContact(index)}
                      className="text-body-xs text-error font-semibold hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-body-xs text-on-surface-variant italic py-3">
            No secondary department contacts or branch leads added yet.
          </p>
        )}
      </div>

      {/* ── Linked Deals / Pipeline Opportunities ────────────────────────── */}
      {dealsData?.deals && dealsData.deals.length > 0 && (
        <div className="rounded-card border border-border bg-card p-6 shadow-xs flex flex-col gap-4">
          <div className="flex items-center justify-between border-b border-outline/60 pb-4">
            <div>
              <h2 className="text-body-base font-bold text-on-surface flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-primary" />
                Linked Pipeline &amp; Deals ({dealsData.deals.length})
              </h2>
            </div>
          </div>

          <div className="divide-y divide-outline/60">
            {dealsData.deals.map((deal) => (
              <div
                key={deal.id}
                onClick={() => navigate(`/pipeline/${deal.id}`)}
                className="py-3 flex items-center justify-between hover:bg-surface-container-high/30 px-2 rounded-lg cursor-pointer transition"
              >
                <div>
                  <h3 className="font-semibold text-body-sm text-on-surface">{deal.title}</h3>
                  <span className="text-body-xs text-on-surface-variant capitalize">Stage: {deal.stage}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-body-sm text-on-surface block">{fmtMoney(deal.value)}</span>
                  <span className="text-data-xs text-primary font-medium hover:underline inline-flex items-center gap-1">
                    View Pipeline Record <ExternalLink className="h-3 w-3" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SubContact Modal ──────────────────────────────────────────────── */}
      <SubContactFormModal
        open={subContactModalOpen}
        subContact={editingSubContact?.sub}
        loading={updateContactExtended.isPending}
        onClose={() => {
          setSubContactModalOpen(false);
          setEditingSubContact(null);
        }}
        onSave={handleSaveSubContact}
      />

      {/* ── Delete Confirm Dialog ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Contact Record"
        message={`Are you sure you want to delete "${contact.company || contact.name}"? This action cannot be undone.`}
        confirmLabel="Delete Contact"
        danger
        loading={deleteContact.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
}

export default ContactDetailPage;
